/**
 * Reports Feature - Report Definitions
 *
 * Configuration for all available reports in the system
 */

import type { ReportCard } from '../types';
import type { IconName } from '@repo/ui';

/**
 * Base report definitions (without enabled flags)
 * Enabled flags are determined dynamically based on tenant configuration
 */
interface BaseReportDefinition {
  id: string;
  title: string;
  description: string;
  icon: IconName;
  category: string;
  route?: string;
  isComingSoon?: boolean;
}

/**
 * All available report definitions
 */
const BASE_REPORT_DEFINITIONS: BaseReportDefinition[] = [
  {
    id: 'accounts-receivable-exception',
    title: 'Accounts Receivable Exception Report',
    description:
      'Verifies the accuracy and completeness of Clio Manage and QuickBooks Online integration by comparing AR invoices and payment applications to ensure data integrity and identify discrepancies between systems.',
    icon: 'HiTable',
    category: 'Billing',
    route: '/reports/ar',
  },
  {
    id: 'trust-reconciliation',
    title: '3-Way Trust Reconciliation Report',
    description:
      'Verifies accuracy and completeness of Clio Trust transactions synced to QuickBooks Online Trust Bank and Trust Liability accounts by comparing all three data sources to ensure data integrity.',
    icon: 'HiShieldCheck',
    category: 'Compliance',
    route: '/reports/trust',
  },
  {
    id: 'accounts-receivable-aging',
    title: 'Accounts receivable aging report',
    description:
      'The accounts receivable aging report tracks unpaid bills and clients with overdue bills by time period (current, 1-30 days, 31-60 days, 61-90 days, and 91+...',
    icon: 'HiTable',
    category: 'Billing',
    route: '/reports/accounts-receivable-aging',
    isComingSoon: true,
  },
  {
    id: 'billing-report',
    title: 'Billing report',
    description:
      'The billing report provides a snapshot of firm finances, showing unpaid, paid, and overdue invoices. It tracks bill status, due dates, payments,...',
    icon: 'HiCurrencyDollar',
    category: 'Billing',
    route: '/reports/billing',
    isComingSoon: true,
  },
  {
    id: 'client-productivity',
    title: 'Client productivity report',
    description:
      'The client productivity report gives you a breakdown of how much time is being spent for specific clients, and can help you understand how much of your wo...',
    icon: 'HiUsers',
    category: 'Productivity',
    route: '/reports/client-productivity',
    isComingSoon: true,
  },
  {
    id: 'client-transaction',
    title: 'Client transaction report',
    description:
      'The client transaction report gives a detailed view of client trust and operating transactions. This report also shows payments, refunds, transfers, and...',
    icon: 'HiClipboardList',
    category: 'Compliance',
    route: '/reports/client-transaction',
    isComingSoon: true,
  },
  {
    id: 'payment-allocation',
    title: 'Payment allocation report',
    description:
      'The payment allocation report gives you a detailed view of the time and expenses each firm user has collected per matter. Use it to understand what...',
    icon: 'HiCurrencyDollar',
    category: 'Revenue',
    route: '/reports/payment-allocation',
    isComingSoon: true,
  },
  {
    id: 'payment-report',
    title: 'Payment report',
    description:
      'The payment report gives you a detailed view of what payments you are receiving. Use it to understand where you are generating revenue, and where you...',
    icon: 'HiTrendingUp',
    category: 'Revenue',
    route: '/reports/payment-report',
    isComingSoon: true,
  },
  {
    id: 'transaction-report',
    title: 'Transaction report',
    description:
      'The transaction report gives a detailed view of the history of your trust and operating transactions, and what actions you took with those transactions. It als...',
    icon: 'HiClipboardList',
    category: 'Compliance',
    route: '/reports/transaction-report',
    isComingSoon: true,
  },
  {
    id: 'trust-transaction',
    title: 'Trust transaction report',
    description:
      'The trust transaction report gives a detailed view of client trust and operating transactions. This report also shows payments, refunds, transfers, and...',
    icon: 'HiClipboardList',
    category: 'Compliance',
    route: '/reports/trust-transaction',
    isComingSoon: true,
  },
  {
    id: 'user-productivity',
    title: 'User productivity report',
    description:
      'The user productivity report gives you a detailed view of the time and expenses each firm user has collected per matter. Use it to understand what...',
    icon: 'HiUsers',
    category: 'Productivity',
    route: '/reports/user-productivity',
    isComingSoon: true,
  },
  {
    id: 'matter-ledger',
    title: 'Matter Ledger Report',
    description:
      'View detailed transaction history for specific matters including bills, payments, trust transactions, and balances with comprehensive filtering options.',
    icon: 'HiTable',
    category: 'Billing',
    route: '/reports/matter-ledger',
  },
  {
    id: 'matter-statement',
    title: 'Matter Statement Report',
    description:
      'Generate comprehensive account statements for specific matters including opening/closing balances, billing summaries, trust activity, and transaction history.',
    icon: 'HiDocumentText',
    category: 'Billing',
    route: '/reports/matter-statement',
  },
];

/**
 * Generate report cards with enabled flags based on tenant configuration
 */
export function getReportCards(
  isReportingEnabled: boolean,
  isArExceptionReportingEnabled: boolean,
  isTrustReconciliationEnabled: boolean,
  isMatterLedgerReportingEnabled: boolean,
  isMatterStatementReportingEnabled: boolean
): ReportCard[] {
  return BASE_REPORT_DEFINITIONS.map(report => {
    let isEnabled = false;

    // Determine enabled state based on report ID
    if (report.id === 'accounts-receivable-exception') {
      isEnabled = isReportingEnabled && isArExceptionReportingEnabled;
    } else if (report.id === 'trust-reconciliation') {
      isEnabled = isReportingEnabled && isTrustReconciliationEnabled;
    } else if (report.id === 'matter-ledger') {
      isEnabled = isReportingEnabled && isMatterLedgerReportingEnabled;
    } else if (report.id === 'matter-statement') {
      isEnabled = isReportingEnabled && isMatterStatementReportingEnabled;
    } else {
      // For coming soon reports, they are enabled if reporting module is enabled
      // but marked as coming soon
      isEnabled = isReportingEnabled && report.isComingSoon === true;
    }

    return {
      ...report,
      isEnabled,
    };
  });
}

